import { activeFilterCount, UNCATEGORISED, ME, type DeckFilterState } from './deckFilterState.ts';
import { type DeckModelOption } from './useDeckFilters.ts';

/**
 * What the page says when a view comes back empty.
 *
 * "No decks yet" was the only empty state the dashboard had, and by Run 6 there
 * were six ways to get an empty body that had nothing to do with an empty
 * studio: a search that matches nothing, a folder nobody has filed anything
 * under, a status, a model, an updated window, and the bin. All six rendered
 * the same seven words — "Nothing matches this
 * view." — which names none of them and offers no way back.
 *
 * Two rules the sentences follow, and they are why this is data rather than
 * markup:
 *
 * - **Say which filter emptied it, not that something did.** With exactly ONE
 *   axis on, the line names that axis's value; with more than one there is no
 *   honest single culprit, so it says so and the chip row above lists them.
 * - **Every filtered state offers the way out**, wired to the same clear-all
 *   the chip row's button uses. A dead end with a Clear filters button under it
 *   is a dead end you can leave.
 *
 * Pure, so the copy can be asserted without a DOM — which matters here more
 * than usual, since these strings are the only thing seven states differ by.
 */

export type DeckEmptyAction = {
  /** `clear` runs the page's clear-all; `new` opens the new-deck wizard. */
  kind: 'clear' | 'new';
  label: string;
};

export type DeckEmptyStateInfo = {
  /** A stable id for the state, so the headless UI check can name one without
   *  matching on copy. Rendered as `data-empty`. */
  key: string;
  /** One quiet line. Full sentence, British spelling, no exclamation marks. */
  message: string;
  action: DeckEmptyAction | null;
};

/** "Clear filter" when exactly one is on — the button should not offer to clear
 *  a set the user can see is a single thing. */
export function clearLabel(count: number): string {
  return count === 1 ? 'Clear filter' : 'Clear filters';
}

const UPDATED_SENTENCES: Record<Exclude<DeckFilterState['updated'], null>, string> = {
  today: 'Nothing has been edited today.',
  week: 'Nothing has been edited in the last 7 days.',
  month: 'Nothing has been edited in the last 30 days.',
  quarter: 'Nothing has been edited in the last 90 days.',
};

const STATUS_SENTENCES: Record<'published' | 'unpublished', string> = {
  published: 'Nothing has been published yet.',
  unpublished: 'There are no drafts here.',
};

type EmptyStateInput = {
  filters: DeckFilterState;
  /** The model roster, for turning a stored id back into the label the card
   *  shows. A model can be filtered from a URL after its last deck was deleted,
   *  so the raw id is the fallback rather than a blank. */
  models: readonly DeckModelOption[];
  /** How long the bin keeps a deck, for the empty-trash line. */
  trashRetentionDays?: number;
  /** An owner id back to a name, for the Owner sentence. Same fallback as
   *  `models`: an id nobody answers for still reads as itself. */
  personName?: (id: string) => string | null;
};

/**
 * The empty state for the CURRENT view. Callers render it whenever the body
 * they were about to draw has nothing in it.
 */
export function deckEmptyState({
  filters,
  models,
  trashRetentionDays = 7,
  personName,
}: EmptyStateInput): DeckEmptyStateInfo {
  const count = activeFilterCount(filters);
  const clear = (key: string, message: string): DeckEmptyStateInfo => ({
    key,
    message,
    action: { kind: 'clear', label: clearLabel(count) },
  });

  // The bin is not a narrowing of the grid, it REPLACES it, so it gets its own
  // line and its own way out — "Clear filters" would be a strange thing to
  // press when what you want is the decks back.
  if (filters.status === 'trash') {
    const days = Math.max(1, Math.round(trashRetentionDays));
    return {
      key: 'trash',
      message: `The trash is empty. A deck you delete waits here for ${days} day${days === 1 ? '' : 's'} before it goes for good.`,
      action: { kind: 'clear', label: 'Back to all decks' },
    };
  }

  // Nothing is filtering, so the studio itself is empty. The only useful action
  // is to make something.
  if (count === 0) {
    return {
      key: 'none',
      message: 'No decks yet.',
      action: { kind: 'new', label: 'Create a deck' },
    };
  }

  // More than one axis is on and there is no single one to blame. The chip row
  // directly above already lists them, so the line does not repeat it.
  if (count > 1) return clear('many', 'No decks match these filters.');

  if (filters.query) return clear('query', `No decks match “${filters.query}”.`);
  if (filters.status) return clear('status', STATUS_SENTENCES[filters.status]);
  if (filters.category) {
    // Uncategorised is the absence of filing rather than a folder, so an empty
    // one is good news and reads as such instead of as a barren folder.
    if (filters.category === UNCATEGORISED) return clear('category', 'Every deck is filed in a folder.');
    return clear('category', `There is nothing in ${filters.category} yet.`);
  }
  if (filters.model) {
    const label = models.find((m) => m.id === filters.model)?.label ?? filters.model;
    return clear('model', `No decks were built with ${label}.`);
  }
  if (filters.owner) {
    // The `me` case is the one worth wording properly: it is what a new starter
    // sees on their first day, and "No decks match" would read as a fault.
    if (filters.owner === ME) return clear('owner', 'You do not own any decks yet.');
    const label = personName?.(filters.owner) ?? filters.owner;
    return clear('owner', `${label} does not own any decks.`);
  }
  if (filters.updated) return clear('updated', UPDATED_SENTENCES[filters.updated]);
  // Unreachable while FILTER_AXES and the branches above agree; a sentence
  // rather than a throw, because an empty page is not the place to crash.
  return clear('many', 'No decks match these filters.');
}
